import { type PropsWithChildren, useEffect, useId, useState } from "react";
import { useModuleBEM, useUiStyles } from "@b2bportal/core-themes";
import { CoreUiComponents } from "@b2bportal/core-types";
import { Root, Thumb } from "@radix-ui/react-switch";
import clsx from "clsx";
import defaultStyles from "./Switch.module.scss";

export type SwitchProps = {
  className?: string;
  id?: string;
  label: string | JSX.Element;
  defaultChecked?: boolean;
  checked?: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
};

export const Switch = ({
  className,
  id: idProps,
  label,
  checked: controlledChecked,
  onChange,
  disabled = false,
  defaultChecked = false,
}: PropsWithChildren<SwitchProps>) => {
  const styles = useUiStyles(CoreUiComponents.Switch, defaultStyles);
  const [block, cn] = useModuleBEM(styles, CoreUiComponents.Switch);

  const [checked, setCheck] = useState(controlledChecked ?? defaultChecked);

  useEffect(() => {
    if (controlledChecked != null) setCheck(controlledChecked);
  }, [controlledChecked]);

  const handleCheckedChange = (checked: boolean) => {
    setCheck(checked);
    onChange?.(checked);
  };

  const reactId = useId();
  const id = idProps ?? reactId;

  return (
    <div className={clsx(className, block)}>
      {label && (
        <label className={cn("label")} htmlFor={id}>
          {label}
        </label>
      )}

      <Root
        className={cn("root", { checked })}
        id={id}
        checked={checked}
        onCheckedChange={handleCheckedChange}
        disabled={disabled}
      >
        <Thumb className={cn("thumb", { checked })} />
      </Root>
    </div>
  );
};
